import { NoticeDialogLink } from "@/components/privacy/Notice";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Laptop, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { AssessFrame, AssessDone } from "@/pages/assess/AssessPage";
import { WsWorkspace } from "@/pages/workSample/WsWorkspace";
import { PageTour } from "@/components/app/PageTour";

function Intro({ token, info, onStart }) {
  const [agree, setAgree] = useState(info.consented);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try { if (!info.consented) await api.post(`/public/work-sample/${token}/consent`, { version: info.consent_version }); await api.post(`/public/work-sample/${token}/start`); onStart(); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <article className="panel space-y-5 p-5 sm:p-8" data-tour="pt-ws-intro" data-testid="ws-intro">
      <div className="flex justify-end"><PageTour /></div>
      <p className="text-sm text-ink-muted">{info.job_title}</p>
      <h1 className="text-3xl font-semibold tracking-tight">Your practical task</h1>
      <div className="space-y-3 text-sm text-ink-secondary">
        <p className="flex gap-2"><Clock size={16} className="mt-0.5 shrink-0 text-brand" />You'll have <strong className="text-ink">{info.time_limit_minutes} minutes</strong> once you press Start. The timer keeps running if you close the page, and your work is submitted automatically when time is up. Please start before {new Date(info.deadline_at).toLocaleString()}.</p>
        <p className="flex gap-2"><Info size={16} className="mt-0.5 shrink-0 text-brand" />{info.disclosure}</p>
        {info.laptop_notice && <p className="flex gap-2 rounded-lg bg-caution-tint p-3 text-caution" data-testid="ws-laptop-notice"><Laptop size={16} className="mt-0.5 shrink-0" />This task works best on a laptop or desktop.</p>}
      </div>
      <label className="flex items-start gap-3 rounded-[6px] border border-line bg-subtle p-4 text-sm text-ink-secondary">
        <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} disabled={info.consented} className="mt-0.5" data-tour="pt-ws-consent-checkbox" data-testid="ws-consent-checkbox" /><span>{info.consent_text} <NoticeDialogLink testId="ws-notice-link" /></span>
      </label>
      <Button className="h-12 w-full text-base sm:w-auto sm:px-8" disabled={!agree || busy} onClick={start} data-tour="pt-ws-start-btn" data-testid="ws-start-btn">{info.status === "in_progress" ? "Resume" : "Start the timer"}</Button>
    </article>
  );
}

export default function WorkSamplePage() {
  const { token } = useParams();
  const [step, setStep] = useState(null);
  const { data: info, isLoading, error } = useQuery({ queryKey: ["ws-public", token], queryFn: () => get(`/public/work-sample/${token}`), retry: false, staleTime: Infinity });
  if (isLoading) return <AssessFrame><Skel className="h-72" /></AssessFrame>;
  if (error) {
    const code = error.response?.status;
    return <AssessFrame><div className="panel space-y-2 p-6" data-testid={code === 410 ? "ws-expired" : "ws-invalid"}><h1 className="text-2xl font-semibold">{code === 410 ? "This task has expired" : "This link isn't valid"}</h1><p className="text-ink-secondary">{errMsg(error)}</p></div></AssessFrame>;
  }
  if (info.status === "submitted" || step === "done") return <AssessFrame info={info}><div data-testid="ws-done"><AssessDone info={info} /></div></AssessFrame>;
  const cur = step || (info.status === "in_progress" ? "work" : "intro");
  return (
    <AssessFrame info={info}>
      {cur === "intro" ? <Intro token={token} info={info} onStart={() => setStep("work")} /> : <WsWorkspace token={token} types={info.submission_types} onDone={() => setStep("done")} />}
    </AssessFrame>
  );
}
